import { useState, useEffect, useRef } from "react";
import { bannerService } from "./bannerService";

export const useBanners = () => {
  const [banners, setBanners] = useState({});
  const eventSourceRef = useRef(null);

  const visibleBanners = Object.values(banners).filter(
    (banner) =>
      banner.status === "active" &&
      localStorage.getItem(`hideBanner_${banner.id}`) !== "true"
  );

  const hideBanner = (bannerId) => {
    localStorage.setItem(`hideBanner_${bannerId}`, "true");
    setBanners((prevBanners) => {
      const updatedBanners = { ...prevBanners };
      delete updatedBanners[bannerId];
      return updatedBanners;
    });
  };

  const handleSSEMessage = (event) => {
    try {
      const outerData = JSON.parse(event.data);
      const bannerData = JSON.parse(outerData.message);

      setBanners((prevBanners) => {
        const updatedBanners = { ...prevBanners };
        if (
          bannerData.operation === "delete" ||
          (bannerData.operation === "update" && bannerData.status !== "active")
        ) {
          delete updatedBanners[bannerData.id];
        } else {
          updatedBanners[bannerData.id] = {
            id: bannerData.id,
            title: bannerData.title,
            message: bannerData.message,
            status: bannerData.status,
            style: bannerData.style || "info",
          };
        }
        return updatedBanners;
      });
    } catch (error) {
      console.error("Error parsing SSE message:", error);
    }
  };

  const handleSSEError = (error) => {
    console.error("Banner SSE error:", error);
  };

  useEffect(() => {
    let mounted = true;

    const fetchBanners = async () => {
      try {
        const data = await bannerService.fetchAllBanners();
        if (!mounted) return;

        const activeBanners = data.reduce((acc, banner) => {
          if (banner.status === "active") {
            acc[banner.id] = banner;
          }
          return acc;
        }, {});
        setBanners(activeBanners);
      } catch (error) {
        console.error("Error fetching banners:", error);
      }
    };

    // Setup SSE connection
    const setupConnection = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      eventSourceRef.current = bannerService.setupSSEConnection(
        handleSSEMessage,
        handleSSEError
      );
      eventSourceRef.current.onmessage = handleSSEMessage;
    };

    fetchBanners();
    setupConnection();

    // Cleanup function
    return () => {
      mounted = false;
      if (eventSourceRef.current) {
        console.debug("Closing Banner SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Handle visibility change to reconnect if needed
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !eventSourceRef.current) {
        const source = bannerService.setupSSEConnection(
          handleSSEMessage,
          handleSSEError
        );
        source.onmessage = handleSSEMessage;
        eventSourceRef.current = source;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return { visibleBanners, hideBanner };
};
